import { useState, useEffect } from 'react'
import { useGameStore } from '@/store/gameStore'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { backdropVariants, SPRING_REVEAL, useMotionVariants, modalVariants, radialFlashVariants } from '../motion/motionTokens'
import { useTopModal } from '../modals/useTopModal'
import { AngledButton } from './broadcast'
import { ModalCloseButton } from './ModalCloseButton'
import { CelebrationParticles } from '../celebrations/particles'

// Generic cinematic reveal for narrative "story beats" (StorySystem). Shows the
// front of the pendingStoryBeats queue; "Continuar"/"Empezar" advances to the
// next. Generalises the old RegionIntroModal — the region intro is now just a
// gameStart beat. Sits at z-index 210/211 (above level-up/construction reveals).
export function StoryBeatModal() {
  const beat = useGameStore(s => s.pendingStoryBeats[0] ?? null)
  const dismiss = useGameStore(s => s.dismissStoryBeat)
  const top = useTopModal()
  const modal = useMotionVariants(modalVariants)
  const backdrop = useMotionVariants(backdropVariants)
  const reduced = useReducedMotion()
  const [imgOk, setImgOk] = useState(true)

  const meta = beat?.modal ?? null
  useEffect(() => { setImgOk(true) }, [meta?.heroImage])

  const gradient = meta?.gradient ?? ['#1b2c3f', '#0d1520']

  return (
    <AnimatePresence>
      {meta && top === 'storyBeat' && (
        <>
          <motion.div
            variants={backdrop}
            initial="initial"
            animate="enter"
            exit="exit"
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', zIndex: 210 }}
          />

          <motion.div
            variants={modal}
            initial="initial"
            animate="enter"
            exit="exit"
            className="pointer-events-auto"
            style={{
              position: 'fixed', top: '50%', left: '50%', x: '-50%', y: '-50%',
              zIndex: 211,
              background: 'var(--bg-surface)',
              border: '1px solid color-mix(in srgb, var(--gold) 35%, transparent)',
              borderRadius: '16px',
              overflow: 'hidden auto',
              maxHeight: '90dvh',
              width: 'min(460px, 92vw)',
              textAlign: 'center',
              boxShadow: 'var(--shadow-lg), inset 0 3px 0 0 var(--gold)',
            }}
          >
            <ModalCloseButton onClick={dismiss} />
            <motion.div
              key={beat?.id}
              initial={{ scale: 1.12, filter: 'brightness(1.5)' }}
              animate={{ scale: 1, filter: 'brightness(1)' }}
              transition={{ ...SPRING_REVEAL, delay: 0.1 }}
              style={{
                position: 'relative', aspectRatio: '16 / 9', overflow: 'hidden',
                background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`,
              }}
            >
              {imgOk && meta.heroImage && (
                <img
                  src={meta.heroImage}
                  alt={meta.title}
                  onError={() => setImgOk(false)}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              )}

              {/* Cinematic flourish on reveal: a gold broadcast flash + spark
                  burst (both respect prefers-reduced-motion — particles self-skip) */}
              {!reduced && (
                <motion.div
                  aria-hidden
                  variants={radialFlashVariants}
                  initial="initial"
                  animate="enter"
                  style={{
                    position: 'absolute', inset: 0, pointerEvents: 'none',
                    background: 'radial-gradient(circle at 50% 45%, rgba(245,197,66,0.45) 0%, transparent 60%)',
                  }}
                />
              )}
              <div aria-hidden style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
                <CelebrationParticles />
              </div>

              <div style={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(180deg, transparent 35%, rgba(13,21,32,0.92) 100%)',
                display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
                padding: '0.9rem 1.25rem', textAlign: 'left',
              }}>
                <div style={{
                  fontSize: '0.72rem', color: 'var(--gold)',
                  letterSpacing: '2.5px', textTransform: 'uppercase', fontWeight: 700,
                }}>
                  {meta.kicker}
                </div>
                <div style={{
                  fontSize: '1.5rem', fontWeight: 800, color: '#fff',
                  fontFamily: 'var(--font-titles)', textShadow: '0 1px 4px rgba(0,0,0,0.6)',
                }}>
                  {meta.title}
                </div>
              </div>
            </motion.div>

            <div style={{ padding: '1.25rem 1.5rem 1.5rem' }}>
              <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                style={{
                  fontSize: '0.9rem', lineHeight: 1.6, color: 'var(--text-secondary)',
                  marginBottom: '1.25rem', textAlign: 'left',
                }}
              >
                {meta.body}
              </motion.p>

              <AngledButton tone="gold" block onClick={dismiss}>
                {meta.cta ?? 'Continuar'}
              </AngledButton>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
